import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Buildings, CheckCircle, Receipt, Clock, IdentificationCard } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { company: "", contact_name: "", phone: "", email: "", employees: "", needs: "" };
const inputCls = "border-2 border-ink rounded-none h-12 bg-white text-ink focus-visible:ring-2 focus-visible:ring-brandblue";

const PERKS = [
  { Icon: Receipt, title: "One monthly invoice", desc: "All employee rides & deliveries billed once a month — no petty cash, no receipts to chase." },
  { Icon: Clock, title: "Priority 24/7 service", desc: "Your team gets front-of-line pickups for shifts, airport runs and urgent deliveries." },
  { Icon: IdentificationCard, title: "Account-managed", desc: "A named contact, agreed rates and simple monthly statements for your finance team." },
];

export const Corporate = () => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company.trim() || !f.contact_name.trim() || !f.phone.trim()) {
      toast.error("Please add your company, contact name and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/corporate`, { ...f, email: f.email || null });
      setDone(true);
      toast.success("Request sent! We'll be in touch to set up your account.");
    } catch {
      toast.error("Couldn't send — please call or email us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="corporate" data-testid="corporate-section" className="py-20 sm:py-28 bg-ink text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sun mb-3 flex items-center gap-2">
            <Buildings weight="fill" size={16} /> Corporate accounts
          </p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Move your team.<br /><span className="text-sun">Bill it monthly.</span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed max-w-md">
            Set up a corporate account for employee transportation or business deliveries — staff ride and send parcels on the company tab, and you settle one simple invoice each month.
          </p>
          <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-sun">Put it all on the account</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              "Prescription pick-ups", "Deliveries", "Blood / medical delivery",
              "Regular scheduled runs", "Pick-up & drop-off", "Urgent parcels",
              "Employee rides", "Staff parties & events",
            ].map((tag) => (
              <span key={tag} data-testid={`corp-usecase-${tag}`}
                className="text-xs font-semibold border border-white/25 text-white/85 px-3 py-1.5">
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-8 space-y-4">
            {PERKS.map((p) => (
              <div key={p.title} className="flex gap-3">
                <span className="w-10 h-10 shrink-0 bg-sun text-ink border-2 border-sun flex items-center justify-center">
                  <p.Icon weight="fill" size={20} />
                </span>
                <div>
                  <p className="font-bold">{p.title}</p>
                  <p className="text-white/60 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="corporate-success" className="bg-white text-ink border-2 border-sun p-10 flex flex-col items-start">
            <CheckCircle weight="fill" size={56} className="text-brandblue" />
            <h3 className="font-display font-extrabold text-3xl tracking-tight mt-4">Request received!</h3>
            <p className="mt-3 text-ink/70 leading-relaxed">
              Thanks — we'll reach out to {f.contact_name.split(" ")[0]} to set up {f.company}'s monthly account and agree your rates.
            </p>
            <button data-testid="corporate-reset-btn" onClick={() => { setDone(false); setF(EMPTY); }}
              className="mt-6 bg-ink text-white font-bold px-6 py-3 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">
              Send another
            </button>
          </motion.div>
        ) : (
          <form data-testid="corporate-form" onSubmit={submit} className="bg-white text-ink border-2 border-sun p-6 sm:p-8">
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-5">Set up a corporate account</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Input data-testid="corp-company" value={f.company} onChange={set("company")} placeholder="Company name *" className={inputCls} />
              <Input data-testid="corp-contact" value={f.contact_name} onChange={set("contact_name")} placeholder="Contact name *" className={inputCls} />
              <Input data-testid="corp-phone" value={f.phone} onChange={set("phone")} placeholder="Phone *" className={inputCls} />
              <Input data-testid="corp-email" type="email" value={f.email} onChange={set("email")} placeholder="Work email" className={inputCls} />
              <Input data-testid="corp-employees" value={f.employees} onChange={set("employees")} placeholder="Approx. rides / month" className={`${inputCls} sm:col-span-2`} />
            </div>
            <div className="mt-5">
              <Textarea data-testid="corp-needs" value={f.needs} onChange={set("needs")} rows={3}
                placeholder="What do you need? (employee transport, deliveries, airport runs…)"
                className="border-2 border-ink rounded-none focus-visible:ring-2 focus-visible:ring-brandblue" />
            </div>
            <button data-testid="corporate-submit-btn" type="submit" disabled={loading}
              className="mt-6 w-full bg-sun text-ink font-bold text-lg py-4 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-150 disabled:opacity-60">
              {loading ? "Sending…" : "Request corporate account"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
